import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IItemForGetShortInfoWithStatus, UserRoles } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './RestaurantsToHideMultipleSelect.module.scss';

interface IRestaurantsToHideMultipleSelect {
	setIDsOfRestaurantsWhereToHide: (value: string[]) => void;
	IDSelectedCustomer: Nullable<string>;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	IDsSelectedRestaurant: string[];
	IDsOfRestaurantsWhereToHide: string[];
	initialIDsOfRestaurantsWhereToHide: string[];
}

const RestaurantsToHideMultipleSelect: React.FC<IRestaurantsToHideMultipleSelect> = ({
	setIDsOfRestaurantsWhereToHide,
	IDSelectedCustomer,
	fetchedRestaurants,
	IDsSelectedRestaurant,
	IDsOfRestaurantsWhereToHide,
	initialIDsOfRestaurantsWhereToHide,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	// const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, string>(
	// 	IDSelectedCustomer ? getRestaurantsShortInfo : null,
	// 	IDSelectedCustomer || '',
	//  MenuApiVersion.v1,
	// );
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

	const restaurants = useMemo(() => {
		if (IDsSelectedRestaurant.length && fetchedRestaurants.length) {
			const filteredRestaurants = fetchedRestaurants.filter((el) => IDsSelectedRestaurant.includes(el.id));
			return filteredRestaurants;
		}
		return [];
	}, [fetchedRestaurants, IDsSelectedRestaurant]);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = IDsOfRestaurantsWhereToHide.filter((el) => el !== id);
			setIDsOfRestaurantsWhereToHide(updatedSelectedItems);
		},
		[IDsOfRestaurantsWhereToHide, setIDsOfRestaurantsWhereToHide],
	);

	useEffect(() => {
		if (isFirstRender && fetchedRestaurants.length) {
			setIDsOfRestaurantsWhereToHide(initialIDsOfRestaurantsWhereToHide);
			setIsFirstRender(false);
		}
	}, [fetchedRestaurants]);

	const restaurantsList = useMemo(() => {
		return IDsOfRestaurantsWhereToHide.map((el) => {
			const label = fetchedRestaurants.find((item) => item.id === el)?.name || '';
			const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
			return (
				<SelectedItem
					key={el}
					label={label}
					onDelete={!hasAccess && !accessibleRestaurantIds?.includes(el) ? undefined : () => handleRemove(el)}
				/>
			);
		});
	}, [IDsOfRestaurantsWhereToHide, fetchedRestaurants, loggedUser, hasAccess, handleRemove]);

	return (
		<div className={style.container} data-test="RestaurantsToHideMultipleSelect">
			<div className={style.field}>
				<MultipleSelect
					data={restaurants || []}
					setIDsOfRestaurantsWhereToHide={setIDsOfRestaurantsWhereToHide}
					IDSelectedCustomer={IDSelectedCustomer}
					IDsOfRestaurantsWhereToHide={IDsOfRestaurantsWhereToHide}
					IDsSelectedRestaurant={IDsSelectedRestaurant}
					initialIDsOfRestaurantsWhereToHide={initialIDsOfRestaurantsWhereToHide}
				/>
			</div>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(RestaurantsToHideMultipleSelect);
